"use client";

import React, { useState, useRef, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";

const AboutSmile: React.FC = () => {
  const [isInView, setIsInView] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: '150px' }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative w-full bg-gray-900 py-12 sm:py-16"
      role="region"
      aria-labelledby="about-smile-heading"
    >
      {/* Background Image - lazy load when in viewport */}
      <div className="absolute inset-0">
        {isInView ? (
          <>
            <Image
              src="/images/custom_images_banner.webp"
              alt="Smile4Me dental office background"
              fill
              className="object-cover object-center"
              sizes="100vw"
              quality={60}
              priority={false}
            />
            <div className="absolute inset-0 bg-black/40"></div>
          </>
        ) : (
          <div className="absolute inset-0 bg-gray-900"></div>
        )}
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-8 lg:px-12 flex flex-col items-center text-white space-y-8">
        <h2
          id="about-smile-heading"
          className="
            inline-block text-center
            text-3xl sm:text-4xl lg:text-5xl font-semibold text-white
            after:content-[''] after:block after:w-[200px] after:h-[3px] after:mx-auto after:my-5
            after:bg-[linear-gradient(to_right,#AE7D2F_0%,#ECD05B_50%,#AE7D2F_100%)]
          "
        >
          Why Choose Smile4Me Dental Care?
        </h2>

        <p className="text-base sm:text-lg lg:text-xl text-center leading-relaxed">
          Smile4Me Dental Care is proud to be the most trusted{" "}
          <Link
            href="https://maps.google.com/maps?cid=8192593597492375843"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline transition-all duration-300 ease-in-out"
          >
            dentist in Astoria
          </Link>
          , where advanced technology, personalized care, and a genuine
          commitment to this community come together under one roof. Here&apos;s
          what sets us apart:
        </p>

        <ul className="mx-auto text-base sm:text-lg list-disc pl-6 space-y-4 max-w-3xl">
          <li>
            <strong className="font-semibold">
              Comprehensive In-House Care:
            </strong>{" "}
            From routine cleanings and preventive care to{" "}
            <Link
              href="/services/implant-dentistry"
              className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline transition-all duration-300 ease-in-out"
            >
              dental implants
            </Link>
            , full-mouth reconstruction, and cosmetic dentistry, everything is
            handled in-house by Dr. Khaimov and his team.
          </li>
          <li>
            <strong className="font-semibold">
              State-of-the-Art Technology:
            </strong>{" "}
            CEREC same-day crowns, CBCT 3D imaging, and LANAP laser therapy
            deliver faster, more accurate, and more comfortable results than
            traditional dental methods.
          </li>
          <li>
            <strong className="font-semibold">Flexible Financing:</strong> We
            accept most major insurance plans and offer up to 2 years of
            no-interest financing, because outstanding dental care should be
            accessible to every Astoria family.
          </li>
        </ul>

        <p className="text-base sm:text-lg lg:text-xl text-center leading-relaxed">
          Astoria is more than where{" "}
          <Link
            href="/our-office"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F] bg-clip-text text-transparent hover:underline transition-all duration-300 ease-in-out"
          >
            our office
          </Link>{" "}
          is located; it&apos;s the community we&apos;re part of. Whether
          you&apos;ve lived here for decades or just moved to the neighborhood,
          Smile4Me Dental Care is here to be your long-term dental home.
        </p>

        <div className="mt-8">
          <Link
            href="/contact"
            className="
              uppercase font-bold text-lg tracking-wide text-white
              py-3 px-8
              bg-gradient-to-r from-[#AE7D2F] via-[#ECD05B] to-[#AE7D2F]
              hover:from-[#ECD05B] hover:via-[#AE7D2F] hover:to-[#ECD05B]
              rounded-lg
              shadow-lg hover:shadow-inner
              [text-shadow:1px_1px_2px_rgba(0,0,0,0.5)]
              hover:scale-105
              transition-all duration-300 ease-in-out
              min-h-[44px] min-w-[44px]
              inline-flex items-center justify-center
            "
            aria-label="Book an appointment with Smile4Me"
          >
            Book an Appointment
          </Link>
        </div>
      </div>
    </section>
  );
};

export default AboutSmile;
